import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Award, Sparkles } from "lucide-react";
import { FACULTY_TYPES } from "@/data/faculty";
import { useFacultyRoster } from "@/hooks/useFacultyRoster";

const AWARD_BADGES = [
  { src: "/badges/qahe-excellence-education-2026.png", alt: "QAHE Excellence in Education Award 2026-2027", wide: true },
  { src: "/badges/gcsb-dfgcsb.webp", alt: "GCSB Distinguished Fellow Academic (DFGCSB)" },
  { src: "/badges/cybersecurity-excellence-2026-winner.png", alt: "Cybersecurity Excellence Awards 2026 Winner" },
  { src: "/badges/thinkers360-top10-architecture.png", alt: "Thinkers360 Top 10 Thought Leader - Architecture" },
  { src: "/badges/thinkers360-top25-security.png", alt: "Thinkers360 Top 25 Thought Leader - Security" },
  { src: "/badges/thinkers360-top50-it-leadership.png", alt: "Thinkers360 Top 50 Thought Leader - IT Leadership" },
];

export default function FeaturedFaculty() {
  // Chief first, then guest/adjunct by configured order
  const roster = useFacultyRoster();
  const sorted = [...roster].sort(
    (a, b) => (FACULTY_TYPES[a.type]?.order ?? 99) - (FACULTY_TYPES[b.type]?.order ?? 99),
  );
  const chief = sorted.find((f) => f.type === "chief");
  const others = sorted.filter((f) => f.type !== "chief");

  if (!chief) return null;

  return (
    <section
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      data-testid="home-featured-faculty"
    >
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-1 inline-flex items-center gap-1.5">
            <Sparkles size={13} className="text-[var(--gold-deep)]" /> Featured faculty
          </div>
          <h2 className="font-display text-3xl sm:text-4xl">Learn from chartered security leaders</h2>
          <p className="mt-2 text-sm text-zinc-600 max-w-2xl">
            Every CSI programme is delivered live by faculty who have led converged security at the executive level.
          </p>
        </div>
        <Link
          to="/instructors"
          className="chip inline-flex items-center gap-1.5"
          data-testid="home-faculty-see-all"
        >
          Meet the faculty <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid lg:grid-cols-12 gap-5">
        {/* Chief instructor spotlight */}
        <article
          className="lg:col-span-7 rounded-3xl overflow-hidden border border-[var(--border-warm)] bg-[var(--cream-card)] grid sm:grid-cols-5 group"
          data-testid={`faculty-spotlight-${chief.slug}`}
        >
          <Link
            to="/instructors"
            className="sm:col-span-2 relative block aspect-[4/5] sm:aspect-auto bg-[var(--navy-deep)] overflow-hidden"
          >
            <img
              src={chief.image}
              alt={`${chief.name} - ${chief.title}`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 via-black/20 to-transparent sm:hidden">
              <div className="text-[10px] font-bold tracking-widest uppercase text-[var(--gold-soft)]">
                {chief.title}
              </div>
              <div className="font-display text-base text-white leading-tight mt-0.5">{chief.name}</div>
            </div>
          </Link>
          <div className="sm:col-span-3 p-5 sm:p-6 flex flex-col">
            <div className="eyebrow mb-1.5 hidden sm:block">{chief.title}</div>
            <h3 className="font-display text-xl sm:text-2xl leading-tight">
              <Link to="/instructors" className="hover:text-[var(--terracotta)] transition-colors">
                {chief.name}
              </Link>
            </h3>
            <p className="mt-2 text-sm text-zinc-700 leading-relaxed">{chief.blurb}</p>

            {chief.qualifications?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-zinc-700">
                {chief.qualifications.slice(0, 3).map((q) => (
                  <li key={q} className="flex items-start gap-2">
                    <Award size={12} className="text-[var(--gold-deep)] mt-0.5 shrink-0" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="faculty-spotlight-awards">
              {AWARD_BADGES.map((b) => (
                <img key={b.src} src={b.src} alt={b.alt} title={b.alt} loading="lazy"
                     className={`object-contain rounded-md bg-white p-0.5 ${b.wide ? "h-12 sm:h-14 w-auto" : "h-14 sm:h-16 w-auto"}`}/>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-[var(--border-warm)]">
              <Link
                to="/instructors"
                className="btn-ink px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid={`faculty-spotlight-profile-${chief.slug}`}
              >
                View full profile <ArrowRight size={13} />
              </Link>
              {chief.linkedin && (
                <a
                  href={chief.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  className="chip inline-flex items-center gap-1.5"
                  data-testid={`faculty-spotlight-linkedin-${chief.slug}`}
                >
                  <Linkedin size={12} /> LinkedIn
                </a>
              )}
            </div>
          </div>
        </article>

        {/* Secondary tile - guest/adjunct roster or CTA when none yet */}
        <aside className="lg:col-span-5 grid gap-4">
          {others.length === 0 ? (
            <div
              className="rounded-3xl bg-[var(--navy-deep)] text-white p-6 sm:p-8 flex flex-col"
              data-testid="faculty-roster-cta"
            >
              <div className="eyebrow !text-[var(--gold-soft)] mb-2">Guest faculty roster</div>
              <h3 className="font-display text-2xl sm:text-3xl leading-tight">
                A rotating panel of practitioners from across the EU.
              </h3>
              <p className="mt-3 text-sm text-zinc-300 max-w-md">
                CSI brings guest lecturers from leading OT, physical and cyber-resilience teams into every cohort - tabletop drills, governance labs and executive case clinics.
              </p>
              <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                {["OT & ICS", "Governance", "Executive"].map((t) => (
                  <div
                    key={t}
                    className="rounded-lg border border-white/10 bg-white/5 py-2 text-[10px] font-semibold tracking-wider text-[var(--gold-soft)] uppercase"
                  >
                    {t}
                  </div>
                ))}
              </div>
              <Link
                to="/instructors"
                className="btn-terracotta mt-6 self-start px-4 py-2 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                data-testid="faculty-roster-cta-btn"
              >
                See faculty page <ArrowRight size={13} />
              </Link>
            </div>
          ) : (
            others.slice(0, 2).map((f) => (
              <Link
                key={f.slug}
                to="/instructors"
                className="rounded-2xl border border-[var(--border-warm)] bg-white p-4 flex items-center gap-4 hover:border-[var(--ink)] transition-colors"
                data-testid={`faculty-tile-${f.slug}`}
              >
                <div className="w-16 h-16 rounded-full overflow-hidden bg-[var(--navy-deep)] shrink-0">
                  {f.image ? <img src={f.image} alt={f.name} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full grid place-items-center text-[var(--gold-soft)] font-display text-xl">{f.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</div>}
                </div>
                <div className="min-w-0">
                  <div className="eyebrow text-[10px]">{FACULTY_TYPES[f.type]?.label || "Faculty"}</div>
                  <div className="font-display text-base leading-tight truncate">{f.name}</div>
                  <div className="text-xs text-zinc-600 truncate">{f.title}</div>
                </div>
              </Link>
            ))
          )}
        </aside>
      </div>
    </section>
  );
}
